import { useState, useEffect } from "react";
import { Bell, BellOff, MapPin, Plane, Clock, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { WebPushTestButton } from "@/components/ios/WebPushTestButton";
import { toast } from "sonner";

interface NotificationPrefs {
  wait_time_alerts: boolean;
  wait_time_threshold: number;
  flight_status_alerts: boolean;
  location_alerts: boolean;
  quiet_hours_start: string;
  quiet_hours_end: string;
}

const defaultPrefs: NotificationPrefs = {
  wait_time_alerts: true,
  wait_time_threshold: 20,
  flight_status_alerts: true,
  location_alerts: false,
  quiet_hours_start: "22:00",
  quiet_hours_end: "06:00",
};

export const NotificationSettings = () => {
  const { user } = useAuth();
  const { isNative, isSupported, permissionStatus, requestPermission } = usePushNotifications();
  const [prefs, setPrefs] = useState<NotificationPrefs>(defaultPrefs);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const loadPrefs = async () => {
      const { data } = await supabase
        .from("notification_preferences")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (data) {
        setPrefs({
          wait_time_alerts: data.wait_time_alerts,
          wait_time_threshold: data.wait_time_threshold,
          flight_status_alerts: data.flight_status_alerts,
          location_alerts: data.location_alerts,
          quiet_hours_start: data.quiet_hours_start || "22:00",
          quiet_hours_end: data.quiet_hours_end || "06:00",
        });
      }
      setLoading(false);
    };

    loadPrefs();
  }, [user]);

  const savePrefs = async (newPrefs: NotificationPrefs) => {
    if (!user) return;
    setSaving(true);

    const { error } = await supabase
      .from("notification_preferences")
      .upsert(
        {
          user_id: user.id,
          ...newPrefs,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" }
      );

    setSaving(false);

    if (error) {
      toast.error("Failed to save notification settings");
    } else {
      toast.success("Notification settings saved");
    }
  };

  const updatePref = <K extends keyof NotificationPrefs>(
    key: K,
    value: NotificationPrefs[K]
  ) => {
    const newPrefs = { ...prefs, [key]: value };
    setPrefs(newPrefs);
    savePrefs(newPrefs);
  };

  const handleLocationAlertToggle = async (enabled: boolean) => {
    if (!enabled || !isNative) {
      updatePref("location_alerts", enabled);
      return;
    }

    try {
      const { Geolocation } = await import("@capacitor/geolocation");
      const permission = await Geolocation.requestPermissions();
      if (permission.location !== "granted") {
        toast.error("Enable location access in iOS Settings to use location alerts.");
        return;
      }

      updatePref("location_alerts", true);
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 10000,
      });
      await supabase.functions.invoke("report-location", {
        body: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyMeters: position.coords.accuracy,
        },
      });
    } catch {
      toast.error("Could not enable location alerts right now.");
    }
  };

  const handleEnableNotifications = async () => {
    const granted = await requestPermission();
    if (granted) {
      toast.success("Notifications enabled!");
    } else {
      toast.error("Permission denied. Enable notifications in your device settings.");
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6 flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notification Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Push permission status */}
        {isNative && isSupported && permissionStatus !== "granted" && (
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <BellOff className="h-5 w-5 text-primary" />
              <div className="flex-1">
                <p className="text-sm font-medium">Push notifications are off</p>
                <p className="text-xs text-muted-foreground">
                  Enable to get alerts about wait times and flights
                </p>
              </div>
              <Button size="sm" onClick={handleEnableNotifications}>
                Enable
              </Button>
            </div>
          </div>
        )}

        {!isNative && (
          <div className="space-y-4">
            <div className="bg-muted/50 border border-border rounded-xl p-4">
              <p className="text-base text-muted-foreground">
              Push notifications are available when you install the app on your device.
              Your preferences will be saved and applied when you install.
              </p>
            </div>
            <WebPushTestButton />
          </div>
        )}

        {/* Wait time alerts */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <Label htmlFor="wait-time-alerts" className="cursor-pointer">
              <div>
                <p className="text-sm font-medium">Wait time alerts</p>
                <p className="text-xs text-muted-foreground">
                  Get notified when wait times change at saved airports
                </p>
              </div>
            </Label>
          </div>
          <Switch
            id="wait-time-alerts"
            checked={prefs.wait_time_alerts}
            onCheckedChange={(v) => updatePref("wait_time_alerts", v)}
          />
        </div>

        {/* Threshold slider */}
        {prefs.wait_time_alerts && (
          <div className="pl-7 space-y-2">
            <Label className="text-xs text-muted-foreground">
              Alert when wait exceeds: <span className="text-foreground font-semibold">{prefs.wait_time_threshold} min</span>
            </Label>
            <Slider
              value={[prefs.wait_time_threshold]}
              onValueChange={(v) => updatePref("wait_time_threshold", v[0])}
              min={5}
              max={60}
              step={5}
              className="w-full"
            />
          </div>
        )}

        {/* Flight status alerts */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Plane className="h-4 w-4 text-muted-foreground" />
            <Label htmlFor="flight-alerts" className="cursor-pointer">
              <div>
                <p className="text-sm font-medium">Flight status alerts</p>
                <p className="text-xs text-muted-foreground">
                  Gate changes, delays, and boarding notifications
                </p>
              </div>
            </Label>
          </div>
          <Switch
            id="flight-alerts"
            checked={prefs.flight_status_alerts}
            onCheckedChange={(v) => updatePref("flight_status_alerts", v)}
          />
        </div>

        {/* Location alerts */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <Label htmlFor="location-alerts" className="cursor-pointer">
              <div>
                <p className="text-sm font-medium">Location-based alerts</p>
                <p className="text-xs text-muted-foreground">
                  Auto-show wait times when near an airport
                </p>
              </div>
            </Label>
          </div>
          <Switch
            id="location-alerts"
            checked={prefs.location_alerts}
            onCheckedChange={handleLocationAlertToggle}
          />
        </div>

        {saving && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Saving...
          </p>
        )}
      </CardContent>
    </Card>
  );
};
